import { Api } from '@monorepo/shared/apiClient';
import {
  LogEntryCacheEvents,
  logEntryCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/logEntryCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, usePaginatedQuery } from 'react-query';

type KeyParams = Api.FetchEdpAiSubmissionsParams;

const KEY_START = 'edpAiSubmissions';
export const createQueryKey = ({
  organizationId,
  logId,
  projectId,
  requestedPage,
  perPage,
}: KeyParams) =>
  [
    KEY_START,
    organizationId,
    logId,
    projectId,
    { requestedPage, perPage },
  ] as const;

type Fetcher = Api.DataHookQueryFn<
  typeof createQueryKey,
  typeof Api.fetchEdpAiSubmissions
>;

type UseEdpAiSubmissionsParams = Partial<Api.FetchEdpAiSubmissionsParams> & {
  config?: QueryConfig<Api.FetchEdpAiSubmissionsResponse, Api.ErrorResponse>;
};

const fetcher: Fetcher = (
  _: string,
  organizationId: string,
  logId: string,
  projectId: string,
  { requestedPage, perPage }: Pick<KeyParams, 'requestedPage' | 'perPage'>,
) =>
  Api.fetchEdpAiSubmissions({
    organizationId,
    logId,
    projectId,
    requestedPage,
    perPage,
  });

export const useEdpAiSubmissions = ({
  config: inputConfig,
  organizationId,
  logId,
  projectId,
  requestedPage,
  perPage,
}: UseEdpAiSubmissionsParams) => {
  const isEnabled = !!organizationId && !!projectId && !!logId;
  const config = getQueryConfig(inputConfig, isEnabled);
  const key = isEnabled
    ? createQueryKey({
        organizationId,
        logId,
        projectId,
        requestedPage,
        perPage,
      })
    : undefined;
  const {
    resolvedData: {
      data: edpAiSubmissions = undefined,
      pagination = undefined,
    } = {},
    ...queryInfo
  } = usePaginatedQuery(key, fetcher, config);
  return {
    edpAiSubmissions,
    pagination,
    ...queryInfo,
  };
};

type InvalidateCacheParams = {
  organizationId: string;
  logId?: string;
  projectId?: string;
};

async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([
      KEY_START,
      keyParams.organizationId,
      keyParams.logId,
      keyParams.projectId,
    ]),
  );
}

const cacheRegisterInvalidator = {
  hookName: 'useEdpAiSubmissions',
  callback: (keyParams: InvalidateCacheParams) => invalidateCache(keyParams),
};

logEntryCacheRegister(
  [
    LogEntryCacheEvents.CREATE,
    LogEntryCacheEvents.UPDATE,
    LogEntryCacheEvents.DELETE,
  ],
  cacheRegisterInvalidator,
);
